import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import i18n from '../i18n'

const UploadPortalContext = createContext(null)

const SESSION_KEY = 'upload_portal_auth'
// Metadata (id, name, size) of files uploaded in this browser, shared by all its tabs and kept
// after the tab is closed. The content is on the server; the ids let the server move exactly
// these files to the new session after the old one expired. The server only accepts files that are
// not submitted yet, belong to the same case/email and are not older than 24h, so entries are
// dropped here after the same 24h.
const DOCS_KEY = 'upload_portal_docs'
const DOCS_TTL_MS = 24 * 60 * 60 * 1000

function readDocs() {
  try {
    const docs = JSON.parse(localStorage.getItem(DOCS_KEY) || '[]')
    if (!Array.isArray(docs)) return []
    const minSavedAt = Date.now() - DOCS_TTL_MS
    return docs.filter((d) => Number.isInteger(d?.id) && d.savedAt > minSavedAt)
  } catch {
    return []
  }
}

function writeDocs(docs) {
  try {
    if (docs.length > 0) localStorage.setItem(DOCS_KEY, JSON.stringify(docs))
    else localStorage.removeItem(DOCS_KEY)
  } catch {}
}

// Merges, never overwrites: other tabs of the same browser may have their own entries.
function rememberDocs(docs) {
  const stored = readDocs()
  const known = new Set(stored.map((d) => d.id))
  const savedAt = Date.now()
  const added = (docs ?? [])
    .filter((d) => Number.isInteger(d?.id) && !known.has(d.id))
    .map(({ id, name, size }) => ({ id, name, size, savedAt }))
  writeDocs([...stored, ...added])
}

function forgetDocs(ids) {
  const remove = new Set(ids)
  writeDocs(readDocs().filter((d) => !remove.has(d.id)))
}

/** Ids of files uploaded in this browser that have not been submitted yet. */
export function getPendingDocumentIds() {
  return readDocs().map((d) => d.id)
}

const initialState = {
  authenticated: false,
  caseReference: '',
  email: '',
  checklist: null,
  documents: [],
  restoring: true,
}

function loadFromStorage() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    if (raw) return JSON.parse(raw)
  } catch {}
  return null
}

export function UploadPortalProvider({ children }) {
  const [data, setData] = useState(() => {
    const stored = loadFromStorage()
    if (stored) return { ...initialState, ...stored, documents: [], restoring: true }
    return initialState
  })

  useEffect(() => {
    if (!data.restoring) return
    const stored = loadFromStorage()
    if (!stored) {
      setData((prev) => ({ ...prev, restoring: false }))
      return
    }
    fetch('/api/uploadportal/sessioninfo')
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((json) => {
        if (json.locale) i18n.changeLanguage(json.locale === 'de' ? 'de' : 'en')
        rememberDocs(json.documents)
        setData((prev) => ({
          ...prev,
          authenticated: true,
          checklist: json.checklist,
          documents: json.documents ?? [],
          restoring: false,
        }))
      })
      .catch(() => {
        sessionStorage.removeItem(SESSION_KEY)
        setData(({ ...initialState, restoring: false }))
      })
  }, [])

  const login = useCallback((caseReference, email, checklist, documents) => {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify({ caseReference, email }))
    rememberDocs(documents)
    setData((prev) => ({
      ...prev,
      authenticated: true,
      caseReference,
      email,
      checklist,
      documents: documents ?? [],
      restoring: false,
    }))
  }, [])

  const logout = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY)
    // after submit or quit: forget only the files of this session, not those of other tabs
    setData((prev) => {
      forgetDocs(prev.documents.map((d) => d.id))
      return { ...initialState, restoring: false }
    })
  }, [])

  const addDocuments = useCallback((docs) => {
    setData((prev) => {
      const documents = [...prev.documents, ...docs]
      rememberDocs(docs)
      return { ...prev, documents }
    })
  }, [])

  const removeDocument = useCallback((id) => {
    setData((prev) => {
      const documents = prev.documents.filter((d) => d.id !== id)
      forgetDocs([id])
      return { ...prev, documents }
    })
  }, [])

  const value = useMemo(() => ({ data, login, logout, addDocuments, removeDocument }), [data, login, logout, addDocuments, removeDocument])

  return (
    <UploadPortalContext.Provider value={value}>
      {children}
    </UploadPortalContext.Provider>
  )
}

export function useUploadPortal() {
  const ctx = useContext(UploadPortalContext)
  if (!ctx) throw new Error('useUploadPortal must be used within UploadPortalProvider')
  return ctx
}
